import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Calendar, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import apiAuth from "@/api/axios";
import { API } from "@/App";
import { useAuth } from "@/hooks/AuthContext";

export const NOTIFICATION_TYPES = {
  LEAVE_REQUEST: "leave_request",
  SYSTEM_ALERT: "system_alert",
  ANNOUNCEMENT: "announcement",
  TIMESHEET_APPROVAL: "MONTHLY_TIMESHEET_APPROVAL",
  GENERIC: "generic",
};

// Notification model
export class Notification {
  constructor(data) {
    this.id = data.id || data._id;
    this.type = data.type;
    this.title = data.title;
    this.message = data.message;
    this.timestamp = data.timestamp;
    this.read = data.read || false;
    this.isViewed = data.isViewed || false; // Added isViewed
    this.metadata = data.metadata || {};
    this.link = data.link;
  }

  static createFromApiData(apiData, type) {
    switch (type) {
      case NOTIFICATION_TYPES.LEAVE_REQUEST:
        return new Notification({
          id: apiData._id,
          type,
          title: "Leave Request",
          message: `${apiData.user_name} applied for ${apiData.leave_type || apiData.type} leave`,
          timestamp: apiData.reason || "",
          metadata: {
            fromDate: new Date(apiData.start_date).toLocaleDateString("en-GB"),
            toDate: new Date(apiData.end_date).toLocaleDateString("en-GB"),
            leaveType: apiData.leave_type || apiData.type,
            leaveBalance: apiData.leave_balance,
            userName: apiData.user_name,
            userId: apiData.user_id,
            status: apiData.status,
          },
          read: false,
          isViewed: true, // Leave requests shown here are considered viewed
        });

      case NOTIFICATION_TYPES.TIMESHEET_APPROVAL:
        return new Notification({
          id: apiData._id,
          type,
          title: apiData.title || "Timesheet Approved",
          message: apiData.message,
          timestamp: new Date(apiData.createdAt).toLocaleString(),
          read: apiData.isRead || false,
          isViewed: apiData.isViewed || false,
          metadata: apiData.metadata || {},
        });

      default:
        return new Notification({
          id: apiData._id,
          type: type || NOTIFICATION_TYPES.GENERIC,
          title: apiData.title || "Notification",
          message: apiData.message,
          timestamp: new Date(apiData.createdAt).toLocaleString(),
          read: apiData.isRead || false,
          isViewed: apiData.isViewed || false,
          metadata: apiData.metadata || {},
        });
    }
  }

  getIcon() {
    switch (this.type) {
      case NOTIFICATION_TYPES.LEAVE_REQUEST:
        return <Calendar size={14} />;
      case NOTIFICATION_TYPES.SYSTEM_ALERT:
        return <AlertCircle size={14} />;
      case NOTIFICATION_TYPES.TIMESHEET_APPROVAL:
        return <CheckCircle size={14} className="text-green-500" />;
      default:
        return <Bell size={14} />;
    }
  }
}

export function UserNotificationMenu() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const pollingRef = useRef(null);

  const fetchNotifications = async () => {
    if (!user?._id && !user?.id) return;

    try {
      setIsLoading(true);

      let leaveNotifications = [];
      if (user?.role === "ADMIN" || user?.role === "PM") {
        const leaveRes = await apiAuth.get(`${API}/calendar/leave-requests/pending`);
        leaveNotifications = (leaveRes.data.requests || []).map((req) =>
          Notification.createFromApiData(req, NOTIFICATION_TYPES.LEAVE_REQUEST)
        );
      }

      // Fetch generic notifications with limit for optimization
      const notifRes = await apiAuth.get(`${API}/notification`, {
        params: { 
          page: 1, 
          limit: 5, // Optimization: only fetch latest 5
          includeViewed: true 
        } 
      });
      
      const genericNotifications = (notifRes.data.notifications || notifRes.data || []).map((n) =>
        Notification.createFromApiData(n, n.type || NOTIFICATION_TYPES.GENERIC)
      );

      const allNotifications = [...leaveNotifications, ...genericNotifications].map(
        (n, idx) => ({ ...n, uniqueKey: `${n.id}-${idx}` })
      );
      
      // Sort
      allNotifications.sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp));

      setNotifications(allNotifications);
      
      // Fetch unread count from server
      const countRes = await apiAuth.get(`${API}/notification/unread-count?includeViewed=false`);
      setUnreadCount(countRes.data.unreadCount);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      fetchNotifications();
      
      pollingRef.current = setInterval(fetchNotifications, 30000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  const handleLeaveAction = async (id, action) => {
    try {
      await apiAuth.post(`${API}/calendar/leave-requests/${id}/decision`, {
        decision: action === "approved" ? "APPROVED" : "REJECTED",
        approver_id: user?.id,
        approval_justification:
          action === "approved" ? "Approved by manager" : "Declined by manager",
      });

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? {
                ...n,
                read: true,
                metadata: { ...n.metadata, status: action === "approved" ? "APPROVED" : "REJECTED" },
              }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error(`Failed to ${action} leave request`, err);
    }
  };

  const handleMarkAsRead = async (id, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      await apiAuth.post(`${API}/notification/viewed`, { ids: [id] });
      
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, read: true, isViewed: true }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error("Failed to mark notification as read", err);
    }
  };

  const renderNotificationContent = (notification) => {
    const handleClick = () => {
      // Navigate for timesheet approvals
      if (notification.type === NOTIFICATION_TYPES.TIMESHEET_APPROVAL && notification.metadata.userId) {
        navigate(`/admin/hr/${notification.metadata.userId}`);
      }
     
      setIsDropdownOpen(false);
    };

    if (notification.type === NOTIFICATION_TYPES.LEAVE_REQUEST) {
      return (
        <div className="flex justify-between w-full gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{notification.title}</span>
              {!notification.read && <span className="h-2 w-2 rounded-full bg-primary"></span>}
            </div>
            <p className="text-sm text-muted-foreground">{notification.message}</p>
            <p className="text-xs text-muted-foreground mt-1">
              <b>{notification.metadata.fromDate}</b> → <b>{notification.metadata.toDate}</b>
            </p>
            <div className="flex gap-2 mt-1">
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                {notification.metadata.leaveType}
              </span>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                Balance: {notification.metadata.leaveBalance}
              </span>
            </div>
            {notification.timestamp && (
              <span className="text-xs italic text-muted-foreground block mt-1">
                Reason: {notification.timestamp}
              </span>
            )}
          </div>

          {notification.metadata.status === "PENDING" &&
          (user?.role === "ADMIN" || user?.role === "PM") ? (
            <div className="flex flex-col gap-2 items-end">
              <Button
                size="sm"
                className="w-20 bg-green-500 hover:bg-green-700 text-white"
                onClick={() => handleLeaveAction(notification.id, "approved")}
              >
                Approve
              </Button>
              <Button
                size="sm"
                className="w-20"
                variant="destructive"
                onClick={() => handleLeaveAction(notification.id, "declined")}
              >
                Decline
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {notification.metadata.status === "APPROVED" ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : notification.metadata.status === "REJECTED" ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : (
                <span className="text-xs italic text-muted-foreground">Awaiting manager review</span>
              )}
              {notification.metadata.status && (
                <span className="text-xs text-muted-foreground">{notification.metadata.status}</span>
              )}
            </div>
          )}
        </div>
      );
    }

    // Timesheet & generic notifications
    return (
      <div
        onClick={handleClick}
        className="flex flex-col gap-1 w-full cursor-pointer group relative"
      >
        <div className="flex justify-between items-start">
          <span className="font-medium text-sm pr-6">{notification.title}</span>
          {!notification.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0"></span>}
        </div>
        <p className="text-sm text-muted-foreground">{notification.message}</p>
        <span className="text-xs text-muted-foreground">{notification.timestamp}</span>
        
        {!notification.read && (
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-0 right-0 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => handleMarkAsRead(notification.id, e)}
            title="Mark as read"
          >
            <CheckCircle size={14} className="text-muted-foreground hover:text-primary" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell size={18} />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-96 max-w-full sm:w-[28rem] shadow-lg">
        <div className="flex items-center justify-between p-2 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
        </div>

        <div className="max-h-96 overflow-y-auto divide-y">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground text-sm">Loading notifications...</div>
          ) : notifications.length > 0 ? (
            notifications.map((n) => (
              <DropdownMenuItem
                key={n.uniqueKey}
                className="p-0 cursor-pointer bg-transparent"
              >
                {renderNotificationContent(n)}
              </DropdownMenuItem>
            ))
          ) : (
            <div className="p-4 text-center text-muted-foreground text-sm">No notifications</div>
          )}
        </div>

        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer text-center justify-center"
              onClick={() => { navigate("/notifications"); setIsDropdownOpen(false); }}
            >
              View all notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
